import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import Sidebar from "../sidebar/sidebar";
import Header from "../header/header";
import { injectAuditStyles } from "./auditStyles";
import { useAuditData } from "./useAuditData";
import { HighlightText } from "./AuditIcons";
import { useLocation } from "react-router-dom";

/* ─────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────────── */
const fmt = (v) => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return isNaN(n) ? String(v) : n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const injectPageStyles = () => {
  if (document.getElementById("al-styles")) return;
  const s = document.createElement("style");
  s.id = "al-styles";
  s.textContent = `
    * { box-sizing: border-box; }
    .al-input {
      height: 32px; border-radius: 7px; border: 1px solid #e2e8f0;
      background: #f8fafc; font-size: 12px; color: #0f172a;
      padding: 0 10px; outline: none; font-family: inherit;
      transition: border .15s, box-shadow .15s;
    }
    .al-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.1); background: #fff; }
    .al-select {
      height: 32px; border-radius: 7px; border: 1px solid #e2e8f0;
      background: #f8fafc; font-size: 12px; color: #475569;
      padding: 0 10px; cursor: pointer; outline: none; font-family: inherit;
      transition: border .15s;
    }
    .al-select:focus { border-color: #3b82f6; }
    .al-select.active { border-color: #3b82f6; background: #eff6ff; color: #1d4ed8; font-weight: 600; }
    .al-row { transition: background .1s; cursor: default; }
    .al-row:hover { background: #f8faff !important; }
    .al-row:hover .al-review { filter: brightness(.93); }
    @keyframes alShimmer {
      0%   { background-position: 100% 0; }
      100% { background-position: -100% 0; }
    }
    .al-shimmer {
      background: linear-gradient(90deg, #f8fafc 25%, #f0f4f8 50%, #f8fafc 75%);
      background-size: 400% 100%;
      animation: alShimmer 1.3s ease infinite;
    }
  `;
  document.head.appendChild(s);
};

/* ─────────────────────────────────────────────────────────────
   Module Multiselect — no icons, clean checkbox list
───────────────────────────────────────────────────────────── */
const ModuleMultiSelect = ({ options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = (mod) => {
    const next = new Set(selected);
    next.has(mod) ? next.delete(mod) : next.add(mod);
    onChange(next);
  };

  const isActive = selected.size > 0;
  const label = isActive
    ? selected.size === 1 ? [...selected][0] : `${selected.size} modules`
    : "All Modules";

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          height: 32, padding: "0 28px 0 10px", borderRadius: 7,
          border: `1px solid ${isActive ? "#3b82f6" : "#e2e8f0"}`,
          background: isActive ? "#eff6ff" : "#f8fafc",
          color: isActive ? "#1d4ed8" : "#475569",
          fontSize: 12, fontWeight: isActive ? 600 : 400,
          cursor: "pointer", fontFamily: "inherit",
          display: "flex", alignItems: "center",
          minWidth: 130, position: "relative",
          whiteSpace: "nowrap", transition: "all .15s",
        }}
      >
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
        <span style={{
          position: "absolute", right: 9, top: "50%",
          transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
          transition: "transform .15s", fontSize: 8, color: "#94a3b8",
        }}>▼</span>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 300,
          background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8,
          boxShadow: "0 6px 24px rgba(0,0,0,.09)", minWidth: 190,
          maxHeight: 240, overflow: "auto", padding: "4px 0",
        }}>
          {/* All option */}
          <label style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "7px 12px", cursor: "pointer", fontSize: 12,
            fontWeight: selected.size === 0 ? 600 : 400,
            color: selected.size === 0 ? "#1d4ed8" : "#475569",
            background: selected.size === 0 ? "#eff6ff" : "transparent",
            borderBottom: "1px solid #f1f5f9",
          }}>
            <input
              type="checkbox"
              checked={selected.size === 0}
              onChange={() => onChange(new Set())}
              style={{ accentColor: "#3b82f6", width: 13, height: 13 }}
            />
            All Modules
          </label>
          {options.map((mod) => {
            const checked = selected.has(mod);
            return (
              <label key={mod} style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "7px 12px", cursor: "pointer", fontSize: 12,
                fontWeight: checked ? 600 : 400,
                color: checked ? "#1d4ed8" : "#475569",
                background: checked ? "#eff6ff" : "transparent",
              }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(mod)}
                  style={{ accentColor: "#3b82f6", width: 13, height: 13 }}
                />
                {mod}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Summary stat — just number + label, no card/icon
───────────────────────────────────────────────────────────── */
const Stat = ({ label, value, color }) => (
  <div style={{ textAlign: "center", padding: "0 4px" }}>
    <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
      {value}
    </div>
    <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".06em", marginTop: 3 }}>
      {label}
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────
   Count pill — colored badge for pending/approved/rejected
───────────────────────────────────────────────────────────── */
const Pill = ({ value, color, bg, border }) => {
  if (!value) return <span style={{ fontSize: 12, color: "#d1d5db", fontVariantNumeric: "tabular-nums" }}>—</span>;
  return (
    <span style={{
      display: "inline-block", padding: "2px 9px", borderRadius: 20,
      fontSize: 11.5, fontWeight: 700, fontVariantNumeric: "tabular-nums",
      color, background: bg, border: `1px solid ${border}`,
    }}>
      {value}
    </span>
  );
};

/* ─────────────────────────────────────────────────────────────
   KPI Row
───────────────────────────────────────────────────────────── */
const KpiRow = ({ item, index, searchQuery, onReview }) => {
  const hasPending = (item.totalPending || 0) > 0;

  return (
    <div
      className="al-row"
      style={{
        display: "grid",
        gridTemplateColumns: "44px 1fr 110px 88px 88px 88px 96px",
        alignItems: "center",
        minHeight: 60,
        borderBottom: "1px solid #f1f5f9",
        background: "#fff",
      }}
    >
      {/* Row number */}
      <div style={{ textAlign: "center", fontSize: 11, fontWeight: 600, color: "#d1d5db", fontVariantNumeric: "tabular-nums" }}>
        {index + 1}
      </div>

      {/* KPI name + tags */}
      <div style={{ padding: "10px 12px 10px 0", minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: hasPending || item.module || item.frequency ? 5 : 0 }}>
          {hasPending && (
            <span style={{ marginTop: 7, width: 5, height: 5, minWidth: 5, borderRadius: "50%", background: "#f59e0b", flexShrink: 0 }} />
          )}
          <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", lineHeight: 1.45, letterSpacing: "-0.01em", wordBreak: "break-word" }}>
            <HighlightText text={item.title || `KPI #${item.questionId}`} query={searchQuery} />
          </span>
        </div>
       
      </div>

      {/* Cumulative value */}
      <div style={{ textAlign: "right", paddingRight: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#1e40af", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
          {fmt(item.cumulativeValue)}
        </div>
        {item.unit && item.unit !== "-" && (
          <div style={{ fontSize: 9.5, color: "#94a3b8", marginTop: 3 }}>{item.unit}</div>
        )}
      </div>

      {/* Pending */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Pill value={item.totalPending || 0} color="#92400e" bg="#fef3c7" border="#fde68a" />
      </div>

      {/* Approved */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Pill value={item.totalApproved || 0} color="#166534" bg="#dcfce7" border="#bbf7d0" />
      </div>

      {/* Rejected */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Pill value={item.totalRejected || 0} color="#991b1b" bg="#fee2e2" border="#fecaca" />
      </div>

      {/* Review button */}
      <div style={{ paddingRight: 14 }}>
        <button
          className="al-review"
          onClick={() => onReview(item)}
          style={{
            width: "100%", padding: "6px 0", borderRadius: 7,
            background: hasPending ? "#1d4ed8" : "#f1f5f9",
            color: hasPending ? "#fff" : "#94a3b8",
            border: `1px solid ${hasPending ? "#1d4ed8" : "#e2e8f0"}`,
            fontSize: 12, fontWeight: 600, cursor: "pointer",
            boxShadow: hasPending ? "0 2px 6px rgba(29,78,216,.18)" : "none",
            transition: "all .15s",
          }}
        >
          Review
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Shimmer skeleton
───────────────────────────────────────────────────────────── */
const ShimmerRows = () => (
  <>
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="al-shimmer" style={{ height: 60, borderBottom: "1px solid #f1f5f9", animationDelay: `${i * 0.07}s` }} />
    ))}
  </>
);

/* ─────────────────────────────────────────────────────────────
   Column header
───────────────────────────────────────────────────────────── */
const Col = ({ children, align = "left", color = "#94a3b8", style: sx = {} }) => (
  <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color, textAlign: align, ...sx }}>
    {children}
  </div>
);

/* ─────────────────────────────────────────────────────────────
   Main Page
───────────────────────────────────────────────────────────── */
const AuditList = (props) => {
  const historyRouter = useHistory();
  const location = useLocation();

  const auditType = useMemo(() => {
    const path = location.pathname || "";
    if (path.includes("internal")) return "INTERNAL";
    if (path.includes("external")) return "EXTERNAL";
    return null;
  }, [location.pathname]);

  useEffect(() => {
    injectAuditStyles();
    injectPageStyles();
  }, []);

  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [search,          setSearch]          = useState("");
  const [fyFilter,        setFyFilter]        = useState("");
  const [statusFilter,    setStatusFilter]    = useState("");
  const [selectedModules, setSelectedModules] = useState(new Set());

  const { auditItems, summary, isLoading, error, financialYears, selectedFY, setSelectedFY } =
    useAuditData({ answerFilters: { locationIds: [] }, auditType });

  useEffect(() => {
    if (selectedFY?.id && !fyFilter) setFyFilter(String(selectedFY.id));
  }, [selectedFY?.id]);

  const handleFyChange = (v) => {
    setFyFilter(v);
    const fy = financialYears.find((f) => String(f.id) === String(v));
    if (fy) setSelectedFY({ id: fy.id, name: fy.financial_year_value, financial_year_value: fy.financial_year_value });
  };

  const moduleOptions = useMemo(() => {
    const seen = new Set();
    for (const item of auditItems) { if (item.module) seen.add(item.module); }
    return [...seen].sort();
  }, [auditItems]);

  const filtered = useMemo(() => {
    let items = [...auditItems];
    if (search) items = items.filter((i) => (i.title || "").toLowerCase().includes(search.toLowerCase()));
    if (statusFilter) items = items.filter((i) => i.auditStatus === statusFilter);
    if (selectedModules.size > 0) items = items.filter((i) => selectedModules.has(i.module));
    return items;
  }, [auditItems, search, statusFilter, selectedModules]);



 const handleReview = useCallback((item) => {
    historyRouter.push({
        pathname: `/audit/answers`,
        state: {
            questionId:   item.questionId,
            locationIds:  item.locations,
            fyId:         selectedFY?.id,
            title:        item.title,
            module:       item.module,
            unit:         item.unit,
            questionType: item.questionType,
            auditType,                        
        },
    });
}, [historyRouter, selectedFY, auditType]);
  const hasActiveFilters = !!(search || statusFilter || selectedModules.size > 0);
  const clearAll = () => { setSearch(""); setStatusFilter(""); setSelectedModules(new Set()); };

  return (
    <div style={{ display: "flex", background: "#f0f2f5", minHeight: "100vh" }}>
      {/* Sidebar */}
      <div style={{
        flex: sidebarExpanded ? "0 0 21%" : "0 0 60px",
        position: "sticky", top: 0, height: "100vh",
        background: "#fff", borderRight: "1px solid #e8ecf2",
        zIndex: 100, transition: "flex .2s",
      }}>
        <Sidebar dataFromParent={props.location?.pathname || "/dashboard"} onSidebarToggle={setSidebarExpanded} />
      </div>

      <main style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
        <Header />

        {/* ── Page header ── */}
        <div style={{ background: "#fff", borderBottom: "1px solid #e8ecf2", padding: "18px 28px 14px", flexShrink: 0 }}>

          {/* Title + stats row */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20, marginBottom: 14, flexWrap: "wrap" }}>
            <div>
              <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.025em" }}>
                Audit Queue
                {auditType && (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: "#3b82f6", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 4, padding: "2px 8px", letterSpacing: ".04em", textTransform: "uppercase", verticalAlign: "middle" }}>
                    {auditType}
                  </span>
                )}
              </h1>
              {selectedFY && (
                <span style={{ fontSize: 11, color: "#64748b", background: "#f1f5f9", padding: "2px 9px", borderRadius: 20, border: "1px solid #e2e8f0", display: "inline-block" }}>
                  {selectedFY.name}
                </span>
              )}
            </div>

            {/* Stats — clean numeric only, no icons/cards */}
            <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
              {[
                { label: "Total", value: summary.total   || auditItems.length, color: "#1e40af" },
                { label: "Pending",  value: summary.pending  || 0,             color: "#b45309" },
                { label: "Approved", value: summary.approved || 0,             color: "#166534" },
                { label: "Rejected", value: summary.rejected || 0,             color: "#991b1b" },
              ].map(({ label, value, color }, i, arr) => (
                <div key={label} style={{ display: "flex", alignItems: "stretch" }}>
                  <div style={{ padding: "6px 18px", textAlign: "center" }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{value}</div>
                    <div style={{ fontSize: 9.5, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".07em", marginTop: 3 }}>{label}</div>
                  </div>
                  {i < arr.length - 1 && (
                    <div style={{ width: 1, background: "#e8ecf2", alignSelf: "stretch", margin: "4px 0" }} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* ── Filters ── */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {/* Search */}
            <input
              className="al-input"
              style={{ width: 200, paddingLeft: 10 }}
              placeholder="Search KPI…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            {/* FY */}
            <select className="al-select" value={fyFilter} onChange={(e) => handleFyChange(e.target.value)}>
              {financialYears.map((fy) => (
                <option key={fy.id} value={fy.id}>{fy.financial_year_value}</option>
              ))}
            </select>

            {/* Status */}
            <select
              className={`al-select${statusFilter ? " active" : ""}`}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="ACCEPTED">Accepted</option>
              <option value="REJECTED">Rejected</option>
            </select>

            {/* Modules */}
            <ModuleMultiSelect options={moduleOptions} selected={selectedModules} onChange={setSelectedModules} />

            {/* Clear */}
            {hasActiveFilters && (
              <button
                onClick={clearAll}
                style={{
                  height: 32, padding: "0 12px", borderRadius: 7, cursor: "pointer",
                  fontSize: 11.5, fontWeight: 600, color: "#dc2626",
                  background: "#fff5f5", border: "1px solid #fecaca",
                  transition: "all .15s",
                }}
              >
                Clear
              </button>
            )}

            <span style={{ marginLeft: "auto", fontSize: 11, color: "#94a3b8", fontVariantNumeric: "tabular-nums" }}>
              {isLoading ? "" : `${filtered.length} KPI${filtered.length !== 1 ? "s" : ""}`}
            </span>
          </div>
        </div>

        {/* ── Table ── */}
        <div style={{ flex: 1, overflow: "auto", padding: "20px 24px 32px" }}>
          <div style={{
            background: "#fff", borderRadius: 10,
            border: "1px solid #e2e8f0", overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,.04), 0 4px 14px rgba(0,0,0,.04)",
          }}>
            {/* Column headers */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "44px 1fr 110px 88px 88px 88px 96px",
              alignItems: "center",
              padding: "10px 0",
              background: "#f8fafc",
              borderBottom: "2px solid #e8ecf2",
              position: "sticky", top: 0, zIndex: 10,
            }}>
              <Col align="center">#</Col>
              <Col>KPI</Col>
              <Col align="right" style={{ paddingRight: 20 }}>Cumulative</Col>
              <Col align="center" color="#b45309">Pending</Col>
              <Col align="center" color="#166534">Approved</Col>
              <Col align="center" color="#991b1b">Rejected</Col>
              <Col>Action</Col>
            </div>

            {/* Content */}
            {isLoading ? (
              <ShimmerRows />
            ) : error ? (
              <div style={{ padding: "56px 24px", textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626", marginBottom: 6 }}>Failed to load audit queue</div>
                <div style={{ fontSize: 12, color: "#94a3b8" }}>{error}</div>
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: "56px 24px", textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>
                  {auditItems.length === 0 ? "No KPIs in the audit queue" : "No KPIs match your filters"}
                </div>
                {hasActiveFilters && (
                  <button onClick={clearAll} style={{ fontSize: 12, color: "#3b82f6", background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: 0 }}>
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              filtered.map((item, idx) => (
                <KpiRow
                  key={item.questionId}
                  item={item}
                  index={idx}
                  searchQuery={search}
                  onReview={handleReview}
                />
              ))
            )}

            {/* Footer count */}
            {!isLoading && !error && filtered.length > 0 && (
              <div style={{
                padding: "10px 20px", borderTop: "1px solid #f1f5f9",
                background: "#f8fafc", display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>
                  Showing {filtered.length} of {auditItems.length} KPIs
                </span>
                <div style={{ display: "flex", gap: 14 }}>
                  {[
                    { label: "pending",  color: "#b45309", count: filtered.reduce((a, i) => a + (i.totalPending  || 0), 0) },
                    { label: "approved", color: "#166534", count: filtered.reduce((a, i) => a + (i.totalApproved || 0), 0) },
                    { label: "rejected", color: "#991b1b", count: filtered.reduce((a, i) => a + (i.totalRejected || 0), 0) },
                  ].filter(x => x.count > 0).map(({ label, color, count }) => (
                    <span key={label} style={{ fontSize: 11, color, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, display: "inline-block" }} />
                      {count} {label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AuditList;